import { useQuery } from '@apollo/client';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import AddBox from '@mui/icons-material/AddBox';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import Check from '@mui/icons-material/Check';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Clear from '@mui/icons-material/Clear';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Edit from '@mui/icons-material/Edit';
import FilterList from '@mui/icons-material/FilterList';
import FirstPage from '@mui/icons-material/FirstPage';
import LastPage from '@mui/icons-material/LastPage';
import ViewIcon from '@mui/icons-material/OpenInBrowser';
import Remove from '@mui/icons-material/Remove';
import SaveAlt from '@mui/icons-material/SaveAlt';
import Search from '@mui/icons-material/Search';
import ViewColumn from '@mui/icons-material/ViewColumn';
import dayjs from 'dayjs';
import MaterialTable from '@material-table/core';
import { useRouter } from 'next/router';
import React, { forwardRef, useEffect, useState } from 'react';
import { GET_DRAFTS } from '../../../helpers/graphql/drafts';
import FirstPostCard from '../FirstPostCard';
import DraftDeleteDialog from './DraftDeleteDialog';
import CancelScheduleIcon from '@mui/icons-material/CancelScheduleSend';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import UnscheduleDraftDialog from './UnscheduleDraftDialog';

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
  View: forwardRef((props, ref) => <ViewIcon {...props} ref={ref} />),
  Unschedule: forwardRef((props, ref) => (
    <CancelScheduleIcon {...props} ref={ref} />
  )),
  Clone: forwardRef((props, ref) => <FileCopyIcon {...props} ref={ref} />),
};

tableIcons.Add.displayName = 'Add';
tableIcons.Check.displayName = 'Check';
tableIcons.Clear.displayName = 'Clear';
tableIcons.Delete.displayName = 'Delete';
tableIcons.DetailPanel.displayName = 'DetailPanel';
tableIcons.Edit.displayName = 'Edit';
tableIcons.Export.displayName = 'Export';
tableIcons.Filter.displayName = 'Filter';
tableIcons.FirstPage.displayName = 'FirstPage';
tableIcons.LastPage.displayName = 'LastPage';
tableIcons.NextPage.displayName = 'NextPage';
tableIcons.PreviousPage.displayName = 'PreviousPage';
tableIcons.ResetSearch.displayName = 'ResetSearch';
tableIcons.Search.displayName = 'Search';
tableIcons.SortArrow.displayName = 'SortArrow';
tableIcons.ThirdStateCheck.displayName = 'ThirdStateCheck';
tableIcons.ViewColumn.displayName = 'ViewColumn';
tableIcons.View.displayName = 'View';
tableIcons.Unschedule.displayName = 'Unschedule';
tableIcons.Clone.displayName = 'Clone';

const DraftTable = ({ sortby }) => {
  const [deleteOpen, setDeleteOpen] = useState();
  const [unscheduleOpen, setUnscheduleOpen] = useState();

  const router = useRouter();

  const handleDelete = id => {
    setDeleteOpen(id);
  };

  const handleUnschedule = id => {
    setUnscheduleOpen(id);
  };

  const [posts, setPosts] = useState();
  const [fullLoaded, setFullLoaded] = useState(false);

  const handleNewPosts = postData => {
    const newPosts = [];
    postData.forEach(post => {
      const { id, title, snapshot, isScheduleFailed } = post;
      const savedate = dayjs(post.savedate);
      const scheduledDate = dayjs(post.scheduledDate);
      const publishedDate = dayjs(post.publishedDate);
      newPosts.push({
        id,
        savedate,
        title,
        snapshot,
        scheduledDate,
        publishedDate,
        isScheduleFailed,
      });
    });
    setPosts(newPosts);
  };

  const handleCompleted = postData => {
    if (!postData || !postData.drafts || !postData.drafts.length === 0) return;
    // Initially fetch 10 posts, then fetch the rest
    handleNewPosts(postData.drafts);
    if (!fullLoaded && postData.drafts.length === 10) {
      setFullLoaded(true);
      // eslint-disable-next-line no-use-before-define
      if (fetchMore)
        fetchMore({
          variables: {
            offset: postData.drafts.length,
            limit: 500,
          },
          // eslint-disable-next-line consistent-return
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) {
              return prev;
            }
            handleNewPosts([...prev.drafts, ...fetchMoreResult.drafts]);
          },
        });
    }
  };

  const { fetchMore, refetch } = useQuery(GET_DRAFTS, {
    variables: {
      limit: 10,
      isScheduled: sortby === 1,
      isPublished: sortby === 2,
    },
    // eslint-disable-next-line no-use-before-define
    onCompleted: handleCompleted,
    ssr: false,
  });

  useEffect(() => {
    if (refetch) {
      refetch();
    }
  }, []);

  if (!posts)
    return (
      <Grid container spacing={0} alignItems="center" justifyContent="center">
        <Grid item lg={12} md={12} sm={12} xs={12} key="loader">
          <div className="p-5 text-center">
            <CircularProgress />
          </div>
        </Grid>
      </Grid>
    );

  if (posts.length === 0)
    return (
      <div className="container d-flex justify-content-center mt-5 mb-5">
        <div style={{ maxWidth: '800px' }}>
          <FirstPostCard />
        </div>
      </div>
    );

  return (
    <>
      <div style={{ maxWidth: '100%' }}>
        <MaterialTable
          icons={tableIcons}
          columns={[
            {
              title: 'Title',
              field: 'title',
              render: rowData => (
                <>
                  {rowData.title || 'Untitled Draft'}
                  {rowData.snapshot && (
                    <div className="badge badge-success ml-2">
                      Snapshot: {rowData.snapshot}
                    </div>
                  )}
                </>
              ),
            },
            {
              title:
                sortby === 1
                  ? 'Scheduled For'
                  : sortby === 2
                  ? 'Published At'
                  : 'Last edit',
              field:
                sortby === 1
                  ? 'scheduledDate'
                  : sortby === 2
                  ? 'publishedDate'
                  : 'savedate',
              type: 'numeric',
              render: rowData =>
                sortby === 1
                  ? rowData.scheduledDate.format('MMM DD YYYY HH:mm')
                  : sortby === 2
                  ? rowData.publishedDate.format('MMM DD YYYY HH:mm')
                  : rowData.savedate.format('MMM DD YYYY HH:mm'),
            },
          ]}
          data={posts}
          title={
            sortby === 1 ? 'Scheduled' : sortby === 2 ? 'Published' : 'Drafts'
          }
          actions={
            sortby === 1
              ? [
                  {
                    icon: tableIcons.Unschedule,
                    tooltip: 'Unschedule',
                    onClick: (event, rowData) => handleUnschedule(rowData.id),
                  },
                ]
              : sortby === 2
              ? [
                  {
                    icon: tableIcons.Clone,
                    tooltip: 'Clone Post',
                    onClick: (event, rowData) =>
                      router.push(
                        `/dashboard/publish?draftId=${rowData.id}&clone=true`,
                      ),
                  },
                  {
                    icon: tableIcons.Delete,
                    tooltip: 'Delete Forever',
                    onClick: (event, rowData) => handleDelete(rowData.id),
                  },
                ]
              : [
                  {
                    icon: tableIcons.Edit,
                    tooltip: 'Edit post',
                    onClick: (event, rowData) =>
                      router.push(`/dashboard/publish?draftId=${rowData.id}`),
                  },
                  {
                    icon: tableIcons.Delete,
                    tooltip: 'Delete Forever',
                    onClick: (event, rowData) => handleDelete(rowData.id),
                  },
                ]
          }
          options={{ pageSize: 10, actionsColumnIndex: -1 }}
        />
      </div>
      <DraftDeleteDialog
        btnText="Delete Draft"
        setOpen={setDeleteOpen}
        id={deleteOpen}
        setPosts={setPosts}
        posts={posts}
      />
      <UnscheduleDraftDialog
        btnText="Revert Scheduled Post to Draft"
        setOpen={setUnscheduleOpen}
        id={unscheduleOpen}
        setPosts={setPosts}
        posts={posts}
      />
    </>
  );
};

export default DraftTable;
